/**
 * batch-61 werkbon-gefactureerd (FO-449, spec 149) — DE pure resolver van het
 * facturatie-spoor op een werkbon: `facturatieStatusVan(werkbon)`.
 *
 * Gedeeld door de backoffice-lijst en -detail, het planbord (FO-472), de
 * Finqle-verzendgate (FO-475), de KPI's/rapportages en de klant-DTO (die alleen
 * de boolean `gefactureerd` doorgeeft). Nergens anders wordt "is deze bon
 * gefactureerd?" afgeleid; de Prisma-fragmenten voor de "te factureren"-queries
 * staan in `apps/api/src/services/werkbon-facturatie.ts` en volgen exact deze
 * regels.
 *
 * Voorrang (hoogste eerst):
 *   1. een EIGEN factuur (`invoice`, niet de interne Finqle-proforma, niet
 *      verwijderd) → GEFACTUREERD via factuur X. Een handmatige markering is dan
 *      "vervangen door factuur X" (bron FACTUUR, gezet door de koppel-hook).
 *   2. de persistente markering (`facturatieStatus` = GEFACTUREERD) met haar bron:
 *      Finqle-verzending, Finqle-API (INVOICED) of handmatig.
 *   3. alleen een interne Finqle-proforma zonder markering (legacy) → via Finqle.
 *   4. interne (kantoor)bon of bewust niet-facturabel (A7) → NIET_FACTURABEL,
 *      sinds FO-474 ALTIJD met reden (intern = INTERN; zonder vastgelegde reden
 *      = "zonder reden - controleren").
 *   5. anders NOG_TE_FACTUREREN.
 *
 * Terugdraaien kan uitsluitend bij een handmatige markering zónder
 * factuurkoppeling (ook geen proforma: die betekent dat de bon al bij Finqle is).
 *
 * Facturen-bronregel (FO-470, batch-63 integratie): het facturatieverzoek
 * (`FinqleBillingRequest`) is de bron voor alles wat richting Finqle ging. De
 * resolver leest het laatste gekoppelde verzoek (`finqleBillingRequests[0]`) in
 * stap 2: staat de bon op "verzonden naar Finqle" en heeft Finqle het verzoek
 * gefactureerd, dan is de bon "gefactureerd via Finqle" met het nummer en de
 * datum van het verzoek. Een bekend nummer wordt nooit door een lege waarde of
 * een Finqle-id vervangen. De STATUS verandert daardoor niet (beide zijn
 * GEFACTUREERD), dus de where-fragmenten in de api-service blijven gelijk.
 */
import {
  WERKBON_FACTURATIE_BRON_LABELS,
  WERKBON_FACTURATIE_STATUS_LABELS,
  nietFacturabelLabel,
  type PlanbordFacturatieFilter,
  type PlanbordFacturatieStatus,
  type PlanbordVergrendelReden,
  type PlanbordVergrendelingDTO,
  type PlanningBoardFacturatieDTO,
  type PlanningBoardFacturatieWeekDTO,
  type WerkbonFacturatieBron,
  type WerkbonFacturatieDTO,
  type WerkbonNietFacturabelReden,
  type WerkbonBundelRefDTO,
} from '@streep/types'
import { utcToAmsterdamWallclock } from './amsterdam-tijd'
import { weekStringFromDate } from './iso-week'
import { isOudeSpiegelrij } from './oude-finqle-rij'

/** De gekoppelde factuur zoals de resolver hem nodig heeft. */
export interface WerkbonFacturatieFactuurRij {
  id: string
  invoiceNumber: string | null
  /** Interne Finqle-proforma (FINQLE-route): geen eigen factuur, telt niet als "via factuur". */
  isFinqleProforma: boolean
  deletedAt?: Date | string | null
}

/**
 * batch-63 integratie (FO-470) - een koppeling van de bon aan een
 * Finqle-facturatieverzoek, zoals `WERKBON_FACTURATIE_SELECT` hem laadt (alleen
 * de laatste, niet-verwijderde koppeling).
 */
export interface WerkbonFinqleVerzoekKoppelingRij {
  billingRequestId: string
  billingRequest: {
    /** FinqleBillingRequestStatus (DRAFT/SUBMITTED/APPROVED/DISAPPROVED/INVOICED). */
    status: string
    finqleInvoiceNumber: string | null
    invoicedAt: Date | string | null
    /** Finqle-UUID; null = alleen lokaal vastgelegd, Finqle heeft het (nog) niet gemeld. */
    finqleId?: string | null
    /** Aantal levende werkbon-koppelingen van het verzoek (bundel-zending > 1). */
    _count?: { werkbonnen: number }
    /**
     * De binnengekomen factuurrij (mailbox, UBL, upload) die de koppelaar aan het
     * verzoek hing (review I2). Een oude proforma of nummerloze spiegelrij telt
     * niet als factuur.
     */
    invoice?: {
      invoiceNumber: string | null
      invoiceDate: Date | string | null
      origin: string
      isFinqleProforma: boolean
      deletedAt: Date | string | null
    } | null
  } | null
}

/**
 * De werkbon-velden die de resolver leest. Laad ze met het gedeelde
 * `WERKBON_FACTURATIE_SELECT` uit de api-service, zodat lijst, detail, planbord,
 * KPI en klant-DTO dezelfde invoer hebben. `invoice` is optioneel voor callers
 * die de relatie niet laden; dan wint de persistente markering en valt een
 * ongeladen `invoiceId` terug op "via factuur" zonder nummer.
 *
 * batch-63 (FO-474, H4): `facturabel`, `facturabelReden` en `isIntern` zijn
 * VERPLICHT. Voorheen waren ze optioneel met een stille default; een select die
 * ze vergat leverde dan zonder foutmelding een verkeerde status. Nu compileert
 * zo'n aanvoer niet meer.
 */
export interface WerkbonFacturatieBronRij {
  invoiceId: string | null
  invoice?: WerkbonFacturatieFactuurRij | null
  facturatieStatus: 'GEFACTUREERD' | null
  facturatieBron: WerkbonFacturatieBron | null
  facturatieReferentie: string | null
  facturatieOp: Date | string | null
  facturatieReden: string | null
  /** A7 — bewust niet-facturabel. */
  facturabel: boolean
  /** FO-032/FO-474 — waarom niet facturabel (null bij een facturabele bon). */
  facturabelReden: WerkbonNietFacturabelReden | null
  /** S3 — interne kantoorbon. */
  isIntern: boolean
  /**
   * batch-63 integratie (FO-470) - het laatste Finqle-facturatieverzoek (0 of 1
   * element). Optioneel: niet geladen = alleen de persistente markering telt.
   */
  finqleBillingRequests?: ReadonlyArray<WerkbonFinqleVerzoekKoppelingRij>
  /**
   * batch-63 integratie (review B2) - de mail van de laatste Finqle-verzending.
   * Een losse verzending heeft die mail als referentie, een zending (bundel,
   * facturatie-overzicht) het zending-id. Optioneel: niet geladen = onbekend.
   */
  finqleVerzondenEmailId?: string | null
}

/**
 * Hoort een Finqle-verzending zonder vastgelegd facturatieverzoek bij één bon?
 * Alleen een losse verzending (referentie = de verzendmail); een zending met
 * meer bonnen trek je niet per bon in. Onbekend (veld niet geladen) = ja.
 */
export function finqleVerzendingIsLos(rij: {
  facturatieReferentie: string | null
  finqleVerzondenEmailId?: string | null
}): boolean {
  if (rij.finqleVerzondenEmailId === undefined) return true
  return (rij.facturatieReferentie ?? null) === (rij.finqleVerzondenEmailId ?? null)
}

/**
 * batch-63 integratie (review B2) - mag een Finqle-VERZENDING nog worden
 * ingetrokken? Sinds FO-470 is er geen proforma meer om te verwijderen; dit is
 * het vervangende pad (met reden en audit, `terugdraaienGefactureerd`). Alleen
 * zolang Finqle er niets mee deed:
 *   - Finqle wees het verzoek af (DISAPPROVED), of
 *   - het verzoek is alleen lokaal bekend (geen Finqle-UUID, niet goedgekeurd)
 *     en draagt alleen deze bon (een bundel trek je niet per bon in).
 * Zonder vastgelegd verzoek (de registratie is fail-soft) alleen een losse
 * verzending (`finqleVerzendingIsLos`).
 * Gefactureerd, goedgekeurd of door Finqle gemeld = een feit bij Finqle: daar
 * corrigeren. De server toetst alle koppelingen; dit predicaat (nieuwste
 * koppeling) stuurt alleen de knop.
 */
export function finqleVerzendingIntrekbaar(
  koppelingen: ReadonlyArray<WerkbonFinqleVerzoekKoppelingRij> | undefined,
  rij: { facturatieReferentie: string | null; finqleVerzondenEmailId?: string | null } = {
    facturatieReferentie: null,
  },
): boolean {
  if (koppelingen === undefined) return false
  const verzoek = koppelingen[0]?.billingRequest ?? null
  if (!verzoek) return finqleVerzendingIsLos(rij)
  if (finqleVerzoekGefactureerd(verzoek)) return false
  if (verzoek.status === 'DISAPPROVED') return true
  const alleenLokaal = (verzoek.finqleId ?? null) === null && verzoek.status !== 'APPROVED'
  return alleenLokaal && (verzoek._count?.werkbonnen ?? 1) <= 1
}

/**
 * batch-63 integratie (review B1/B2) - het facturatieslot van een bon voor
 * wijzigen, heropenen en een nieuwe eigen factuur: een eigen factuur
 * (`invoiceId`) of een Finqle-markering (naar Finqle verzonden, of door Finqle
 * gefactureerd). Sinds FO-470 houdt een naar Finqle verzonden bon geen
 * proforma-factuur (en dus geen `invoiceId`) meer vast; de markering neemt die rol
 * over. Een HANDMATIGE markering sluit niets af: die is terug te draaien en een
 * eigen factuur mag hem vervangen (FO-449).
 */
export type WerkbonFacturatieSlot = 'FACTUUR' | 'FINQLE'

export function werkbonFacturatieSlot(w: {
  invoiceId: string | null
  facturatieStatus: string | null
  facturatieBron: string | null
}): WerkbonFacturatieSlot | null {
  if (w.invoiceId !== null) return 'FACTUUR'
  if (
    w.facturatieStatus === 'GEFACTUREERD' &&
    (w.facturatieBron === 'FINQLE_VERZENDING' || w.facturatieBron === 'FINQLE_API')
  ) {
    return 'FINQLE'
  }
  return null
}

/** De echte factuurrij aan het verzoek, of null (geen, verwijderd, oude Finqle-rij). */
function verzoekFactuur(
  v: WerkbonFinqleVerzoekKoppelingRij['billingRequest'],
): { invoiceNumber: string | null; invoiceDate: Date | string | null } | null {
  const f = v?.invoice ?? null
  if (!f || f.deletedAt != null || f.isFinqleProforma || isOudeSpiegelrij(f)) return null
  return f
}

/**
 * Heeft Finqle het verzoek al gefactureerd (status, datum of nummer bekend, of
 * de factuur is al binnen en aan het verzoek gekoppeld)?
 */
function finqleVerzoekGefactureerd(v: WerkbonFinqleVerzoekKoppelingRij['billingRequest']): boolean {
  if (!v) return false
  return (
    v.status === 'INVOICED' ||
    v.invoicedAt != null ||
    (v.finqleInvoiceNumber ?? '').trim() !== '' ||
    verzoekFactuur(v) !== null
  )
}

const iso = (d: Date | string | null | undefined): string | null => {
  if (d == null) return null
  return typeof d === 'string' ? d : d.toISOString()
}

function labelVoor(bron: WerkbonFacturatieBron, factuurNummer: string | null): string {
  const basis = WERKBON_FACTURATIE_STATUS_LABELS.GEFACTUREERD
  if (bron === 'FACTUUR') {
    return factuurNummer ? `${basis} via factuur ${factuurNummer}` : `${basis} via factuur`
  }
  if (bron === 'HANDMATIG') return `${basis} (${WERKBON_FACTURATIE_BRON_LABELS.HANDMATIG})`
  return `${basis} ${WERKBON_FACTURATIE_BRON_LABELS[bron]}`
}

/** Is de gekoppelde factuur een echte, niet-verwijderde eigen factuur? */
function eigenFactuur(rij: WerkbonFacturatieBronRij): WerkbonFacturatieFactuurRij | null {
  const inv = rij.invoice
  if (!inv) return null
  if (inv.isFinqleProforma) return null
  if (inv.deletedAt) return null
  return inv
}

/** De velden die alleen bij NIET_FACTURABEL iets zeggen. */
const GEEN_NIET_FACTURABEL = { nietFacturabelReden: null, zonderReden: false } as const

/**
 * De reden waarom een bon niet facturabel is. Een interne (kantoor)bon is dat
 * altijd om reden INTERN, ongeacht wat er in het veld staat.
 */
export function nietFacturabelRedenVan(
  rij: Pick<WerkbonFacturatieBronRij, 'isIntern' | 'facturabelReden'>,
): WerkbonNietFacturabelReden | null {
  if (rij.isIntern) return 'INTERN'
  return rij.facturabelReden ?? null
}

/**
 * De afgeleide facturatiestatus van één werkbon. Puur; geen DB, geen klok.
 */
export function facturatieStatusVan(rij: WerkbonFacturatieBronRij): WerkbonFacturatieDTO {
  const persistent = rij.facturatieStatus === 'GEFACTUREERD'
  const persistentBron: WerkbonFacturatieBron | null = persistent
    ? (rij.facturatieBron ?? 'HANDMATIG')
    : null

  // 1. Eigen factuur wint altijd.
  const factuur = eigenFactuur(rij)
  if (factuur) {
    const vervangen = persistentBron === 'FACTUUR' || persistentBron === 'HANDMATIG'
    return {
      status: 'GEFACTUREERD',
      bron: 'FACTUUR',
      referentie: factuur.invoiceNumber,
      op: iso(rij.facturatieOp),
      reden: vervangen ? rij.facturatieReden : null,
      factuur: { id: factuur.id, invoiceNumber: factuur.invoiceNumber },
      vervangenDoorFactuur: vervangen,
      terugdraaibaar: false,
      ...GEEN_NIET_FACTURABEL,
      label: labelVoor('FACTUUR', factuur.invoiceNumber),
    }
  }

  // 2. Persistente markering (Finqle-verzending / Finqle-API / handmatig / vervangen).
  if (persistent && persistentBron) {
    // `invoice` niet geladen maar wel een koppeling: dan is het (bij een niet-
    // Finqle-bron) een eigen factuur waarvan we het nummer niet kennen.
    if (rij.invoice === undefined && rij.invoiceId && persistentBron !== 'FINQLE_VERZENDING') {
      return {
        status: 'GEFACTUREERD',
        bron: 'FACTUUR',
        referentie: rij.facturatieReferentie,
        op: iso(rij.facturatieOp),
        reden: persistentBron === 'FACTUUR' ? rij.facturatieReden : null,
        factuur: { id: rij.invoiceId, invoiceNumber: null },
        vervangenDoorFactuur: persistentBron === 'FACTUUR' || persistentBron === 'HANDMATIG',
        terugdraaibaar: false,
        ...GEEN_NIET_FACTURABEL,
        label: labelVoor('FACTUUR', null),
      }
    }
    // Finqle-route: het facturatieverzoek is de bron (FO-470). Een verzonden bon
    // waarvan Finqle het verzoek al factureerde, is "gefactureerd via Finqle",
    // ook als de markering dat (nog) niet zegt; nummer en datum komen dan van het
    // verzoek. Een bekend nummer wordt nooit door een lege waarde vervangen.
    const verzoek =
      persistentBron === 'FINQLE_VERZENDING' || persistentBron === 'FINQLE_API'
        ? (rij.finqleBillingRequests?.[0]?.billingRequest ?? null)
        : null
    const viaFinqleGefactureerd = finqleVerzoekGefactureerd(verzoek)
    const bron: WerkbonFacturatieBron =
      // Bron FACTUUR zonder (nog) een factuur: de factuur is later verwijderd;
      // de markering staat er nog. Toon 'm eerlijk als handmatig-vervangen.
      persistentBron === 'FINQLE_VERZENDING' && viaFinqleGefactureerd ? 'FINQLE_API' : persistentBron
    // Nummer en datum: eerst wat Finqle meldde, dan de binnengekomen factuurrij (I2).
    const factuurVanVerzoek = verzoekFactuur(verzoek)
    const verzoekNummer =
      (verzoek?.finqleInvoiceNumber ?? '').trim() || (factuurVanVerzoek?.invoiceNumber ?? '').trim()
    const verzoekDatum = verzoek?.invoicedAt ?? factuurVanVerzoek?.invoiceDate ?? null
    // Review I1: een verzend-referentie (zending-/mail-id) is nooit een
    // factuurnummer. Promoveert de verzending naar "via Finqle gefactureerd"
    // zonder bekend nummer, dan is er (nog) geen referentie.
    const referentie =
      bron === 'FINQLE_API'
        ? verzoekNummer !== ''
          ? verzoekNummer
          : persistentBron === 'FINQLE_API'
            ? rij.facturatieReferentie
            : null
        : rij.facturatieReferentie
    const op = bron === 'FINQLE_API' && verzoekDatum ? iso(verzoekDatum) : iso(rij.facturatieOp)
    return {
      status: 'GEFACTUREERD',
      bron,
      referentie,
      op,
      reden: bron === 'HANDMATIG' || bron === 'FACTUUR' ? rij.facturatieReden : null,
      factuur: null,
      vervangenDoorFactuur: bron === 'FACTUUR',
      // Handmatig: altijd terug te draaien. Een Finqle-verzending alleen zolang Finqle
      // hem niet goedkeurde of factureerde (batch-63 integratie, review B2).
      terugdraaibaar:
        rij.invoiceId === null &&
        (bron === 'HANDMATIG' ||
          (bron === 'FINQLE_VERZENDING' && finqleVerzendingIntrekbaar(rij.finqleBillingRequests, rij))),
      ...GEEN_NIET_FACTURABEL,
      label: labelVoor(bron, bron === 'FACTUUR' ? rij.facturatieReferentie : null),
    }
  }

  // 3. Legacy: alleen een interne Finqle-proforma (= al bij Finqle aangeleverd).
  if (rij.invoice?.isFinqleProforma && !rij.invoice.deletedAt) {
    return {
      status: 'GEFACTUREERD',
      bron: 'FINQLE_VERZENDING',
      referentie: rij.invoice.invoiceNumber,
      op: iso(rij.facturatieOp),
      reden: null,
      factuur: null,
      vervangenDoorFactuur: false,
      terugdraaibaar: false,
      ...GEEN_NIET_FACTURABEL,
      label: labelVoor('FINQLE_VERZENDING', null),
    }
  }
  if (rij.invoice === undefined && rij.invoiceId) {
    // Koppeling zonder geladen relatie en zonder markering: eigen factuur.
    return {
      status: 'GEFACTUREERD',
      bron: 'FACTUUR',
      referentie: null,
      op: null,
      reden: null,
      factuur: { id: rij.invoiceId, invoiceNumber: null },
      vervangenDoorFactuur: false,
      terugdraaibaar: false,
      ...GEEN_NIET_FACTURABEL,
      label: labelVoor('FACTUUR', null),
    }
  }

  // 4. Niet facturabel (intern / A7) — FO-474: altijd met de reden erbij.
  if (rij.isIntern === true || rij.facturabel === false) {
    const reden = nietFacturabelRedenVan(rij)
    return {
      status: 'NIET_FACTURABEL',
      bron: null,
      referentie: null,
      op: null,
      reden: null,
      factuur: null,
      vervangenDoorFactuur: false,
      terugdraaibaar: false,
      nietFacturabelReden: reden,
      zonderReden: reden === null,
      label: nietFacturabelLabel(reden),
    }
  }

  // 5. Nog te factureren.
  return {
    status: 'NOG_TE_FACTUREREN',
    bron: null,
    referentie: null,
    op: null,
    reden: null,
    factuur: null,
    vervangenDoorFactuur: false,
    terugdraaibaar: false,
    ...GEEN_NIET_FACTURABEL,
    label: WERKBON_FACTURATIE_STATUS_LABELS.NOG_TE_FACTUREREN,
  }
}

/**
 * batch-63 (FO-472) — het label voor een viewer ZONDER `FACTUREN.canView`: geen
 * factuurnummer (de eigen factuur heet dan "Gefactureerd via factuur"). Met het
 * recht het volledige label. De overige labels dragen nooit een nummer.
 */
export function facturatieLabelVoorViewer(f: WerkbonFacturatieDTO, magFacturenZien: boolean): string {
  if (magFacturenZien) return f.label
  if (f.status === 'GEFACTUREERD' && f.bron === 'FACTUUR') return labelVoor('FACTUUR', null)
  return f.label
}

/**
 * batch-63 (her-review, FO-008) — het facturatie-spoor van een AFGESCHERMDE
 * (besloten) lijstrij: alleen de status blijft; referentie, handmatige reden,
 * factuur en het nummer in het label gaan niet mee, en er valt niets terug te
 * draaien.
 */
export function afgeschermdeFacturatie(f: WerkbonFacturatieDTO): WerkbonFacturatieDTO {
  return {
    ...f,
    referentie: null,
    reden: null,
    factuur: null,
    terugdraaibaar: false,
    label: facturatieLabelVoorViewer(f, false),
  }
}

/** Kortere vorm voor tellers/klant-DTO: is de bon (op welke manier dan ook) gefactureerd? */
export function isWerkbonGefactureerd(rij: WerkbonFacturatieBronRij): boolean {
  return facturatieStatusVan(rij).status === 'GEFACTUREERD'
}

// ─── batch-63 (FO-474) — schrijfpaden: `facturabel: false` nooit zonder reden ─
//
// Elk pad dat een werkbon aanmaakt of zijn facturabel-vlag wijzigt, schrijft
// het resultaat van één van de drie functies hieronder weg - nooit een losse
// `facturabel`-waarde. Het type `FacturabelBesluit` maakt "false zonder reden"
// onschrijfbaar; de drift-guard (`werkbon-facturatie-drift.test.ts`) bewaakt
// dat er geen los pad naast ontstaat.

/** Vlag + reden zoals een schrijfpad ze wegschrijft. `false` draagt ALTIJD een reden. */
export type FacturabelBesluit =
  | { facturabel: true; facturabelReden: null }
  | { facturabel: false; facturabelReden: WerkbonNietFacturabelReden }

/** Een BEWUSTE keuze uit een formulier: het veld is aangeraakt. */
export type FacturabelKeuze =
  | { facturabel: true }
  | { facturabel: false; reden: WerkbonNietFacturabelReden }

export const FACTURABEL_JA: FacturabelBesluit = { facturabel: true, facturabelReden: null }
export const FACTURABEL_INTERN: FacturabelBesluit = { facturabel: false, facturabelReden: 'INTERN' }

/**
 * Vertaalt de (al door het gedeelde Zod-schema gecontroleerde) body-velden naar
 * een keuze. Afwezig = niet aangeraakt = `null` (de server leidt de default af).
 * Een `false` zonder reden kan door het schema niet bestaan; mocht hij toch
 * binnenkomen, dan telt hij als "niet aangeraakt" - nooit als een stille
 * `false` zonder reden.
 */
export function facturabelKeuzeUitInvoer(invoer: {
  facturabel?: boolean | null
  facturabelReden?: WerkbonNietFacturabelReden | null
}): FacturabelKeuze | null {
  if (invoer.facturabel === true) return { facturabel: true }
  if (invoer.facturabel === false && invoer.facturabelReden) {
    return { facturabel: false, reden: invoer.facturabelReden }
  }
  return null
}

/**
 * DE beslissing voor een NIEUWE werkbon (create, bulk, from-client,
 * consignatie, offerte, klantportaal, interne bon, generator):
 *   1. intern (kantoor) → niet facturabel, reden INTERN (wint altijd);
 *   2. een bewuste keuze uit het formulier;
 *   3. de klant-default: uit → niet facturabel, reden KLANT_INSTELLING;
 *   4. anders facturabel.
 */
export function bepaalFacturabelVoorNieuweWerkbon(input: {
  isIntern: boolean
  keuze?: FacturabelKeuze | null
  klantDefault?: boolean | null
}): FacturabelBesluit {
  if (input.isIntern) return FACTURABEL_INTERN
  if (input.keuze) {
    return input.keuze.facturabel
      ? FACTURABEL_JA
      : { facturabel: false, facturabelReden: input.keuze.reden }
  }
  if (input.klantDefault === false) {
    return { facturabel: false, facturabelReden: 'KLANT_INSTELLING' }
  }
  return FACTURABEL_JA
}

/** De facturabel-velden van een bron-werkbon. */
export interface FacturabelBron {
  facturabel: boolean
  facturabelReden: WerkbonNietFacturabelReden | null
  isIntern: boolean
}

/**
 * De keuze die een KOPIE van zijn bron overneemt: de vlag alleen MÉT reden. Een
 * bron die op "niet facturabel" staat zonder reden is een omgevallen vlag
 * (FO-474, H2): die verspreidt zich niet meer (`null` = de kopie volgt de
 * klant-default). Een interne bron geeft geen keuze door: daar wint `isIntern`.
 * Voor generatoren die de klant-default zelf laden (werkbon-create-multiple).
 */
export function kopieFacturabelKeuze(bron: FacturabelBron): FacturabelKeuze | null {
  if (bron.isIntern) return null
  if (bron.facturabel) return { facturabel: true }
  if (bron.facturabelReden) return { facturabel: false, reden: bron.facturabelReden }
  return null
}

/**
 * Een KOPIE (duplicaat, afsplitsen bij dag-verplaatsen, extra inzet) neemt de
 * vlag alleen MÉT reden over (zie `kopieFacturabelKeuze`); de dagelijkse
 * controle meldt een bron met een omgevallen vlag.
 */
export function facturabelVoorKopie(bron: FacturabelBron, klantDefault?: boolean | null): FacturabelBesluit {
  return bepaalFacturabelVoorNieuweWerkbon({
    isIntern: bron.isIntern,
    keuze: kopieFacturabelKeuze(bron),
    klantDefault,
  })
}

/**
 * Bewerken van een bestaande werkbon: alleen als het veld bewust is aangeraakt
 * (`null` = niets wijzigen). Een interne bon blijft altijd INTERN/niet
 * facturabel (een inkomende `true` wordt genegeerd); terug naar facturabel
 * wist de reden.
 */
export function facturabelNaWijziging(
  huidig: { isIntern: boolean },
  keuze: FacturabelKeuze | null,
): FacturabelBesluit | null {
  if (keuze === null) return null
  if (huidig.isIntern) return FACTURABEL_INTERN
  return keuze.facturabel ? FACTURABEL_JA : { facturabel: false, facturabelReden: keuze.reden }
}

// ─── batch-63 (FO-472) — het planbord ────────────────────────────────────────

/** dd-MM-yyyy in NL-tijd (tooltips op het planbord). */
export function formatFacturatieDatumNl(isoOfDatum: string | Date | null): string | null {
  if (!isoOfDatum) return null
  const d = typeof isoOfDatum === 'string' ? new Date(isoOfDatum) : isoOfDatum
  if (Number.isNaN(d.getTime())) return null
  const [jaar, maand, dag] = utcToAmsterdamWallclock(d).ymd.split('-')
  return `${dag}-${maand}-${jaar}`
}

/** De planbord-stand van de resolver-uitkomst (zonder reeks-context). */
export function planbordFacturatieStatusVan(f: WerkbonFacturatieDTO): PlanbordFacturatieStatus {
  if (f.status === 'NIET_FACTURABEL') return 'NIET_FACTURABEL'
  if (f.status === 'NOG_TE_FACTUREREN') return 'TE_FACTUREREN'
  // GEFACTUREERD: een Finqle-VERZENDING is aangeleverd maar nog niet
  // gefactureerd door Finqle; alle andere bronnen zijn echte facturatie.
  return f.bron === 'FINQLE_VERZENDING' ? 'VERZONDEN_FINQLE' : 'GEFACTUREERD'
}

const GOEDKEURING_TOELICHTING: Record<string, string> = {
  GOEDGEKEURD_KLANT: 'goedgekeurd door de klant',
  GOEDGEKEURD_INTERN: 'intern goedgekeurd',
  WACHT_OP_KLANT: 'wacht op de klant',
  WACHT_OP_INTERN: 'wacht op interne goedkeuring',
  AFGEKEURD_KLANT: 'afgekeurd door de klant',
}

/**
 * De velden van de planbord-lock: verplaatsen, hertoewijzen en uit de planning
 * halen weigert de API bij een factuurkoppeling of Finqle-markering (het
 * facturatieslot, batch-63 integratie B2), een verloonde bon of een bon in de
 * goedkeuring.
 */
export interface WerkbonVergrendelInvoer {
  invoiceId: string | null
  facturatieStatus: string | null
  facturatieBron: string | null
  isVerwerktInVerloning: boolean
  goedkeuringStatus: string | null
}

/**
 * DE lock-regel van het planbord (FO-472 + #4). De move-, reassign- en
 * unplan-routes, het vak "In te plannen" en de board-DTO lezen allemaal deze
 * functie, zodat het bord vooraf precies uitlegt wat de API zou weigeren.
 * Volgorde = de volgorde van de bestaande meldingen: factuur, verloning,
 * goedkeuring.
 */
export function werkbonPlanbordVergrendeling(w: WerkbonVergrendelInvoer): PlanbordVergrendelReden | null {
  if (werkbonFacturatieSlot(w) !== null) return 'GEFACTUREERD'
  if (w.isVerwerktInVerloning) return 'VERLOOND'
  if (w.goedkeuringStatus !== null) return 'IN_GOEDKEURING'
  return null
}

/** De korte vorm in de bestaande 422-meldingen ("Werkbon is gefactureerd; ..."). */
export const PLANBORD_VERGRENDEL_KORT: Record<PlanbordVergrendelReden, string> = {
  GEFACTUREERD: 'gefactureerd',
  VERLOOND: 'verwerkt in de verloning',
  IN_GOEDKEURING: 'in goedkeuring',
}

/** Uitleg-zin per goedkeuringsfase (de dialoog vult aan met "... kan daarom niet"). */
const GOEDKEURING_LOCK_UITLEG: Record<string, string> = {
  GOEDGEKEURD_KLANT: 'De werkbon is al goedgekeurd door de klant.',
  GOEDGEKEURD_INTERN: 'De werkbon is al intern goedgekeurd.',
  WACHT_OP_KLANT: 'De werkbon wacht op goedkeuring door de klant.',
  WACHT_OP_INTERN: 'De werkbon wacht op interne goedkeuring.',
  AFGEKEURD_KLANT: 'De werkbon is afgekeurd door de klant en nog niet heropend.',
}

/** De lock als DTO, met een uitleg-zin voor de dialoog op het bord. */
export function planbordVergrendelingVan(w: WerkbonVergrendelInvoer): PlanbordVergrendelingDTO | null {
  const reden = werkbonPlanbordVergrendeling(w)
  if (!reden) return null
  if (reden === 'GEFACTUREERD') {
    return {
      reden,
      uitleg:
        werkbonFacturatieSlot(w) === 'FINQLE'
          ? 'De werkbon is naar Finqle verzonden. Trek de verzending eerst in op de werkbon (zolang Finqle er nog niets mee deed).'
          : 'De werkbon hangt aan een factuur.',
    }
  }
  if (reden === 'VERLOOND') return { reden, uitleg: 'De werkbon is verwerkt in de verloning.' }
  const uitleg = (w.goedkeuringStatus && GOEDKEURING_LOCK_UITLEG[w.goedkeuringStatus]) || 'De werkbon zit in de goedkeuring.'
  return { reden, uitleg }
}

/**
 * batch-63 (her-review Z-N1) — wat "Werktijd aanpassen" (`PATCH
 * /werkbonnen/:id/planning`) bij een vergrendelde bon nog kan. Een factuur
 * blokkeert elke scope. Een bon in de goedkeuring (een verloonde bon is dat
 * altijd al) weigert "alleen deze dag" en wordt in een reeks-scope
 * overgeslagen, terwijl de volgende weken van de reeks wel meegaan.
 */
export type WerktijdRuimte = 'VRIJ' | 'ALLEEN_VOLGENDE_WEKEN' | 'GEBLOKKEERD'

export function werktijdRuimteBijVergrendeling(reden: PlanbordVergrendelReden | null): WerktijdRuimte {
  if (reden === null) return 'VRIJ'
  if (reden === 'GEFACTUREERD') return 'GEBLOKKEERD'
  return 'ALLEEN_VOLGENDE_WEKEN'
}

export interface PlanbordFacturatieOpties extends WerkbonVergrendelInvoer {
  /** Reeks-telling (hoofdbon + weekbonnen); alleen zinvol bij `totaal > 1`. */
  reeks?: { gefactureerd: number; totaal: number } | null
  /** Laatste Finqle-facturatieverzoek van de werkbon (FO-406), indien bekend. */
  verzoekId?: string | null
  /** FACTUREN.canView: zonder dit recht geen nummer, geen factuur-/verzoek-id. */
  magFacturenZien: boolean
  /** batch-65 (FO-484 §C.7) — de verzamelbon van de bon (voor "in bundel VB-…"). */
  bundel?: WerkbonBundelRefDTO | null
}

/**
 * DE vertaling van de resolver-uitkomst naar de planbord-DTO (FO-472). Het bord
 * leidt niets zelf af: status, label en tooltip komen allemaal hieruit.
 */
export function planbordFacturatieVan(
  f: WerkbonFacturatieDTO,
  opties: PlanbordFacturatieOpties,
): PlanningBoardFacturatieDTO {
  const basis = planbordFacturatieStatusVan(f)
  const reeks =
    opties.reeks && opties.reeks.totaal > 1
      ? { gefactureerd: opties.reeks.gefactureerd, totaal: opties.reeks.totaal }
      : null
  const status: PlanbordFacturatieStatus =
    basis === 'TE_FACTUREREN' && reeks && reeks.gefactureerd > 0 && reeks.gefactureerd < reeks.totaal
      ? 'DEELS_GEFACTUREERD'
      : basis

  const zien = opties.magFacturenZien
  // Factuurnummer: eigen factuur (nummer) of Finqle-factuur (referentie bij
  // FINQLE_API). Een verzend-referentie (zending-/mail-id) is géén factuurnummer.
  const factuurNummer = zien
    ? (f.factuur?.invoiceNumber ??
        (f.bron === 'FINQLE_API' || f.bron === 'FACTUUR' ? f.referentie : null))
    : null
  const datum = formatFacturatieDatumNl(f.op)

  let tooltip: string
  switch (status) {
    case 'GEFACTUREERD': {
      const via =
        f.bron === 'HANDMATIG'
          ? 'Gefactureerd (handmatig)'
          : f.bron === 'FINQLE_API'
            ? 'Gefactureerd via Finqle'
            : 'Gefactureerd'
      tooltip =
        `${via}${datum ? ` op ${datum}` : ''}` +
        (factuurNummer ? `, factuur ${factuurNummer}` : '') +
        (zien && f.bron === 'HANDMATIG' && f.referentie ? `, ref. ${f.referentie}` : '')
      break
    }
    case 'VERZONDEN_FINQLE':
      tooltip = `Verzonden naar Finqle${datum ? ` op ${datum}` : ''} (wacht op factuur)`
      break
    case 'DEELS_GEFACTUREERD':
      tooltip =
        `Deels gefactureerd: ${reeks?.gefactureerd ?? 0} van ${reeks?.totaal ?? 0} werkbonnen ` +
        'van deze reeks zijn gefactureerd of naar Finqle verzonden; deze werkbon nog niet'
      break
    case 'TE_FACTUREREN': {
      const fase = opties.goedkeuringStatus ? GOEDKEURING_TOELICHTING[opties.goedkeuringStatus] : null
      tooltip = `Nog te factureren${fase ? ` (${fase})` : ' (nog niet goedgekeurd)'}`
      break
    }
    case 'NIET_FACTURABEL':
      tooltip = f.label
      break
    default: {
      const nooit: never = status
      tooltip = String(nooit)
    }
  }

  // batch-65 (FO-484) — de bundel hoort in dezelfde tooltip ("in bundel VB-…").
  const bundel = opties.bundel
    ? { ...opties.bundel, verzoekId: zien ? opties.bundel.verzoekId : null }
    : null
  if (bundel) {
    tooltip += `, in bundel ${bundel.nummer} (${bundel.aantalWerkbonnen} ${bundel.aantalWerkbonnen === 1 ? 'werkbon' : 'werkbonnen'})`
  }

  return {
    status,
    // Zonder FACTUREN.canView nooit een factuurnummer, ook niet via het label.
    label: status === 'DEELS_GEFACTUREERD' ? 'Deels gefactureerd' : facturatieLabelVoorViewer(f, zien),
    tooltip,
    bundel,
    op: f.op,
    factuurNummer,
    factuurId: zien ? (f.factuur?.id ?? null) : null,
    verzoekId: zien ? (opties.verzoekId ?? null) : null,
    nietFacturabelReden: f.nietFacturabelReden,
    reeks,
    vergrendeling: planbordVergrendelingVan(opties),
  }
}

const DAG_MS = 86_400_000

/** Eén ISO-week in de dagregel-conventie (UTC-middernacht van de NL-dag). */
export interface PlanbordFacturatieWeek {
  /** 'w-jjjj', dezelfde notatie als `Werkbon.weekNr`. */
  weekNr: string
  /** Maandag 00:00:00.000 UTC. */
  van: Date
  /** Zondag 23:59:59.999 UTC. */
  tot: Date
}

/**
 * De volledige ISO-weken (ma t/m zo) die de planbord-periode [start, eind]
 * raken. Een maandweergave geeft zo ook de gedeeltelijk zichtbare randweken als
 * héle week: het weektotaal gaat over de week, niet over het zichtbare stuk.
 */
export function isoWekenInPeriode(start: Date, eind: Date): PlanbordFacturatieWeek[] {
  const dow = start.getUTCDay() || 7
  let maandag = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() - (dow - 1))
  const weken: PlanbordFacturatieWeek[] = []
  while (maandag <= eind.getTime()) {
    const van = new Date(maandag)
    weken.push({ weekNr: weekStringFromDate(van), van, tot: new Date(maandag + 7 * DAG_MS - 1) })
    maandag += 7 * DAG_MS
  }
  return weken
}

/**
 * Het weektotaal "te factureren": per week het aantal DISTINCTE werkbonnen en
 * de som van hun uren binnen die week. De rijen zijn (werkbon, dag, uren) uit
 * één server-side aggregatie over de volledige set.
 */
export function telFacturatieWeken(
  weken: readonly PlanbordFacturatieWeek[],
  rijen: ReadonlyArray<{ werkbonId: string; date: Date; uren: number }>,
): PlanningBoardFacturatieWeekDTO[] {
  return weken.map((w) => {
    const werkbonnen = new Set<string>()
    let uren = 0
    for (const r of rijen) {
      const t = r.date.getTime()
      if (t < w.van.getTime() || t > w.tot.getTime()) continue
      werkbonnen.add(r.werkbonId)
      uren += r.uren
    }
    return {
      weekNr: w.weekNr,
      van: w.van.toISOString().slice(0, 10),
      tot: w.tot.toISOString().slice(0, 10),
      aantalWerkbonnen: werkbonnen.size,
      uren: Math.round(uren * 100) / 100,
    }
  })
}

/**
 * De reeks-telling voor DEELS_GEFACTUREERD: hoofdbon (alleen als hij zelf
 * dagregels heeft) + weekbonnen. Puur; de api levert de aggregaten aan.
 */
export function reeksFacturatieTelling(input: {
  kinderen: { totaal: number; gefactureerd: number } | null
  hoofdbon: { heeftDagregels: boolean; gefactureerd: boolean } | null
}): { totaal: number; gefactureerd: number } {
  const totaal = (input.kinderen?.totaal ?? 0) + (input.hoofdbon?.heeftDagregels ? 1 : 0)
  const gefactureerd =
    (input.kinderen?.gefactureerd ?? 0) +
    (input.hoofdbon?.heeftDagregels && input.hoofdbon.gefactureerd ? 1 : 0)
  return { totaal, gefactureerd }
}

/**
 * Past een planbord-status bij het filter? "Nog te factureren" neemt ook
 * DEELS_GEFACTUREERD mee (deze bon zelf is nog niet gefactureerd);
 * "gefactureerd" neemt ook de Finqle-verzending mee (de bon is de deur uit).
 */
export function planbordFacturatieFilterMatcht(
  status: PlanbordFacturatieStatus,
  filter: PlanbordFacturatieFilter,
): boolean {
  switch (filter) {
    case 'ALLE':
      return true
    case 'TE_FACTUREREN':
      return status === 'TE_FACTUREREN' || status === 'DEELS_GEFACTUREERD'
    case 'GEFACTUREERD':
      return status === 'GEFACTUREERD' || status === 'VERZONDEN_FINQLE'
    default: {
      const nooit: never = filter
      return nooit
    }
  }
}

/**
 * De doorklik van een markering: de eigen factuur, anders het
 * Finqle-facturatieverzoek. `null` als de viewer geen FACTUREN.canView heeft
 * (de server stript de ids dan al) of als er niets te openen is.
 */
export function planbordFacturatieHref(
  f: Pick<PlanningBoardFacturatieDTO, 'factuurId' | 'verzoekId'>,
): string | null {
  if (f.factuurId) return `/facturen/${encodeURIComponent(f.factuurId)}`
  if (f.verzoekId) return `/facturen/finqle-verzoeken/${encodeURIComponent(f.verzoekId)}`
  return null
}

/**
 * De werkbonnenlijst op tab "Te factureren" met dezelfde week (dagregel-datum)
 * en, als het bord op precies één klant filtert, die klant. Exact de set die
 * het weektotaal telt.
 */
export function teFacturerenLijstHref(
  week: Pick<PlanningBoardFacturatieWeekDTO, 'van' | 'tot'>,
  clientId?: string | null,
): string {
  const qs = new URLSearchParams({ tab: 'teFactureren', van: week.van, tot: week.tot })
  if (clientId) qs.set('clientId', clientId)
  return `/werkbonnen?${qs.toString()}`
}

// ─── batch-63 (FO-475) — Finqle-verzendgate "al gefactureerd" ────────────────

export type FinqleVerzendBlokkadeCode = 'AL_GEFACTUREERD' | 'NIET_FACTURABEL'

/** Eerste letter klein (midden in een zin), zonder een factuurnummer te verminken. */
function beginKlein(tekst: string): string {
  return tekst.length === 0 ? tekst : tekst.charAt(0).toLowerCase() + tekst.slice(1)
}

/**
 * Mag deze bon nog (in een bundel) naar Finqle? Een bon die al gefactureerd is
 * (eigen factuur, Finqle-factuur of handmatig) of niet facturabel is, gaat
 * NOOIT stil mee. Een eerdere Finqle-VERZENDING is hier geen blokkade: die
 * handelt de bestaande ALREADY/REDEN_VEREIST-gate af (opnieuw mét reden).
 */
export function finqleVerzendBlokkade(
  f: WerkbonFacturatieDTO,
  werkbonNumber: string,
  /** FACTUREN.canView van de viewer: zonder dit recht noemt de reden geen factuurnummer. */
  opties: { magFacturenZien?: boolean } = {},
): { code: FinqleVerzendBlokkadeCode; reden: string } | null {
  const label = facturatieLabelVoorViewer(f, opties.magFacturenZien ?? false)
  if (f.status === 'NIET_FACTURABEL') {
    return {
      code: 'NIET_FACTURABEL',
      reden: `Werkbon ${werkbonNumber} staat op "${beginKlein(label)}" en gaat niet naar Finqle.`,
    }
  }
  if (f.status === 'GEFACTUREERD' && f.bron !== 'FINQLE_VERZENDING') {
    return {
      code: 'AL_GEFACTUREERD',
      reden: `Werkbon ${werkbonNumber} is al ${beginKlein(label)} en gaat niet nog een keer naar Finqle.`,
    }
  }
  return null
}

// ─── batch-63 (FO-474) — herstel van omgevallen facturabel-vlaggen ────────────

export const FACTURABEL_HERSTEL_CATEGORIEEN = [
  'TERUG_FACTURABEL',
  'TERUG_FACTURABEL_VOOR_FO032',
  'REDEN_UIT_FO032_AUDIT',
  'REDEN_VAN_HOOFDBON',
  'REDEN_KLANT_INSTELLING',
  'REDEN_INTERN',
  'INTERN_VLAG_HERSTELLEN',
  'INTERN_FACTURABEL_UIT',
  'GEFACTUREERD_ONGEMOEID',
  'INTERN_ZONDER_KANTOOR',
] as const
export type FacturabelHerstelCategorie = (typeof FACTURABEL_HERSTEL_CATEGORIEEN)[number]

export const FACTURABEL_HERSTEL_CATEGORIE_UITLEG: Record<FacturabelHerstelCategorie, string> = {
  TERUG_FACTURABEL: 'Omgevallen vlag (geen reden, geen FO-032-audit): terug naar facturabel.',
  TERUG_FACTURABEL_VOOR_FO032:
    'Idem, maar aangemaakt vóór FO-032 (mogelijk bewust uitgesloten zonder redenveld): terug naar facturabel, apart geteld voor de PO.',
  REDEN_UIT_FO032_AUDIT: 'Uit facturatie gehaald via FO-032, reden later gewist: reden uit de audit terugzetten.',
  REDEN_VAN_HOOFDBON: 'Afgesplitst van een hoofdbon die mét reden uit de facturatie is: zelfde reden vastleggen.',
  REDEN_KLANT_INSTELLING: 'Klant staat op "standaard niet facturabel": reden KLANT_INSTELLING vastleggen.',
  REDEN_INTERN: 'Interne (kantoor)bon zonder reden: reden INTERN vastleggen.',
  INTERN_VLAG_HERSTELLEN: 'Type KANTOOR maar niet als intern gemarkeerd (H3): intern + reden INTERN.',
  INTERN_FACTURABEL_UIT: 'Interne (kantoor)bon die op facturabel stond: niet facturabel + reden INTERN.',
  GEFACTUREERD_ONGEMOEID: 'Al gefactureerd: blijft ongemoeid (de factuur wint), alleen gerapporteerd.',
  INTERN_ZONDER_KANTOOR: 'Intern gemarkeerd maar geen type KANTOOR: niet aangeraakt, beslispunt PO.',
}

/** De velden van één kandidaat-rij die de classificatie nodig heeft. */
export interface FacturabelHerstelRij {
  type: string
  isIntern: boolean
  facturabel: boolean
  facturabelReden: WerkbonNietFacturabelReden | null
  /** Resolver-uitkomst: al gefactureerd (stap 1-3)? */
  gefactureerd: boolean
  /** Reden uit de laatste FO-032-uitsluiting die niet is teruggedraaid. */
  fo032Reden: WerkbonNietFacturabelReden | null
  /** De hoofdbon (bij een afgesplitste bon), indien aanwezig. */
  hoofdbon: { facturabel: boolean; facturabelReden: WerkbonNietFacturabelReden | null } | null
  /** `Client.werkbonFacturabelDefault`. */
  klantDefault: boolean
  /** Aangemaakt vóór de eerste FO-032-uitsluiting in de database. */
  aangemaaktVoorFo032: boolean
}

export interface FacturabelHerstelVoorstel {
  categorie: FacturabelHerstelCategorie
  /** De wijziging; `null` = niet aanraken (alleen rapporteren). */
  wijziging: (FacturabelBesluit & { isIntern?: true }) | null
}

/**
 * Het voorstel per kandidaat (volgorde = voorrang). Puur, zodat het rapport en
 * de uitvoering exact dezelfde beslissing nemen.
 */
export function classificeerFacturabelHerstel(rij: FacturabelHerstelRij): FacturabelHerstelVoorstel {
  if (rij.gefactureerd) return { categorie: 'GEFACTUREERD_ONGEMOEID', wijziging: null }
  if (rij.type === 'KANTOOR' && !rij.isIntern) {
    return {
      categorie: 'INTERN_VLAG_HERSTELLEN',
      wijziging: { facturabel: false, facturabelReden: 'INTERN', isIntern: true },
    }
  }
  if (rij.isIntern && rij.type !== 'KANTOOR') {
    return { categorie: 'INTERN_ZONDER_KANTOOR', wijziging: null }
  }
  if (rij.isIntern) {
    return rij.facturabel
      ? { categorie: 'INTERN_FACTURABEL_UIT', wijziging: { facturabel: false, facturabelReden: 'INTERN' } }
      : { categorie: 'REDEN_INTERN', wijziging: { facturabel: false, facturabelReden: 'INTERN' } }
  }
  if (rij.fo032Reden) {
    return {
      categorie: 'REDEN_UIT_FO032_AUDIT',
      wijziging: { facturabel: false, facturabelReden: rij.fo032Reden },
    }
  }
  if (rij.hoofdbon && !rij.hoofdbon.facturabel && rij.hoofdbon.facturabelReden) {
    return {
      categorie: 'REDEN_VAN_HOOFDBON',
      wijziging: { facturabel: false, facturabelReden: rij.hoofdbon.facturabelReden },
    }
  }
  if (!rij.klantDefault) {
    return {
      categorie: 'REDEN_KLANT_INSTELLING',
      wijziging: { facturabel: false, facturabelReden: 'KLANT_INSTELLING' },
    }
  }
  return {
    categorie: rij.aangemaaktVoorFo032 ? 'TERUG_FACTURABEL_VOOR_FO032' : 'TERUG_FACTURABEL',
    wijziging: { facturabel: true, facturabelReden: null },
  }
}
